import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { setAccessTokenGetter } from '../api/client.js'
import { identifyUser, resetAnalytics, trackEvent } from '../analytics/mixpanel.js'

const AuthContext = createContext(null)

export function AuthProvider({ children }) {
  const {
    isAuthenticated,
    isLoading,
    user: auth0User,
    error,
    loginWithRedirect,
    logout: auth0Logout,
    getAccessTokenSilently,
  } = useAuth0()

  const [forbidden, setForbidden] = useState(false)
  const identifiedRef = useRef(null)

  // Wire the axios client's token getter exactly once. The interceptor will
  // call getAccessTokenSilently() per request; Auth0 caches and refreshes
  // tokens internally.
  useEffect(() => {
    setAccessTokenGetter(() => getAccessTokenSilently())
    return () => setAccessTokenGetter(null)
  }, [getAccessTokenSilently])

  // Mixpanel identify on login; reset on logout.
  useEffect(() => {
    if (isAuthenticated && auth0User?.email && identifiedRef.current !== auth0User.sub) {
      identifyUser(auth0User)
      trackEvent('User Logged In', { method: 'auth0' })
      identifiedRef.current = auth0User.sub
    }
    if (!isAuthenticated && identifiedRef.current) {
      resetAnalytics()
      identifiedRef.current = null
    }
  }, [isAuthenticated, auth0User?.email, auth0User?.sub])

  // 401s from the backend bubble through this listener (legacy contract from
  // when we managed tokens ourselves). Trigger an Auth0 logout so the user
  // re-auths cleanly rather than getting stuck with a stale session.
  useEffect(() => {
    const onLogout = () => auth0Logout({ logoutParams: { returnTo: window.location.origin } })
    window.addEventListener('auth:logout', onLogout)
    return () => window.removeEventListener('auth:logout', onLogout)
  }, [auth0Logout])

  useEffect(() => {
    const handle = () => setForbidden(true)
    window.addEventListener('auth:forbidden', handle)
    return () => window.removeEventListener('auth:forbidden', handle)
  }, [])

  const login = useCallback(
    () => loginWithRedirect({ appState: { returnTo: window.location.pathname } }),
    [loginWithRedirect],
  )

  const logout = useCallback(
    () => auth0Logout({ logoutParams: { returnTo: window.location.origin } }),
    [auth0Logout],
  )

  // Keep the existing { email, name, picture } shape consumed by the UI.
  const user = useMemo(() => {
    if (!isAuthenticated || !auth0User) return null
    return {
      email: auth0User.email,
      name: auth0User.name,
      picture: auth0User.picture,
      sub: auth0User.sub,
    }
  }, [isAuthenticated, auth0User])

  return (
    <AuthContext.Provider value={{ user, ready: !isLoading, forbidden, error, login, logout }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider')
  return ctx
}
